import React, { useState, useRef } from 'react';
import api, { errorMessage } from '../api';
import { useAuth } from '../context/AuthContext';
import Avatar from './Avatar';
import { CameraIcon, CloseIcon } from './icons';

/**
 * Skapa inlägg: media, bildtext och vem som får se det.
 *
 * Ligger som ett eget ark i stället för alltid överst i flödet — flödet
 * ska handla om det andra lagt upp, inte om ett tomt formulär.
 */
const HINTS = ['#epa', '#moped', '#cruising', '#bygge', '#fälgar', '#ljudbygge'];

export default function PostComposer({ onPosted, onClose }) {
  const { user } = useAuth();
  const fileRef = useRef(null);
  const captionRef = useRef(null);

  const [content, setContent] = useState('');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [isVideo, setIsVideo] = useState(false);
  const [visibility, setVisibility] = useState('public');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const chooseFile = (event) => {
    const selected = event.target.files[0];
    if (!selected) return;
    if (preview) URL.revokeObjectURL(preview);

    setFile(selected);
    setIsVideo(selected.type.startsWith('video/'));
    setPreview(URL.createObjectURL(selected));
  };

  const clearFile = () => {
    if (preview) URL.revokeObjectURL(preview);
    setFile(null);
    setPreview(null);
    setIsVideo(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  /** Lägger till en hashtagg där markören står. */
  const addTag = (tag) => {
    setContent(prev => (prev.endsWith(' ') || prev === '' ? `${prev}${tag} ` : `${prev} ${tag} `));
    if (captionRef.current) captionRef.current.focus();
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!content.trim() && !file) {
      setError('Skriv något eller välj en bild.');
      return;
    }

    setBusy(true);
    setError('');
    try {
      const data = new FormData();
      data.append('content', content.trim());
      data.append('visibility', visibility);
      if (file) data.append('image', file);

      const res = await api.post('/posts', data);
      clearFile();
      setContent('');
      if (onPosted) onPosted(res.data);
      onClose();
    } catch (err) {
      setError(errorMessage(err, 'Inlägget kunde inte publiceras.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sheet-overlay" onClick={onClose}>
      <form className="sheet sheet--composer" onSubmit={submit} onClick={e => e.stopPropagation()}>
        <div className="composer__top-row">
          <h2 className="sheet__title">Nytt inlägg</h2>
          <button type="button" className="screen__close" onClick={onClose} aria-label="Stäng">
            <CloseIcon size={18} />
          </button>
        </div>

        <button
          type="button"
          className={preview ? 'composer__picker composer__picker--filled' : 'composer__picker'}
          onClick={() => fileRef.current.click()}
        >
          {preview ? (
            isVideo
              ? <video src={preview} muted playsInline />
              : <img src={preview} alt="Förhandsvisning" />
          ) : (
            <>
              <CameraIcon size={30} />
              <span>Välj bild eller film</span>
              <small>Bygget, kvällens tur, nya fälgar…</small>
            </>
          )}
        </button>

        {preview && (
          <button type="button" className="btn btn--sm btn--quiet" onClick={clearFile}>
            Ta bort {isVideo ? 'filmen' : 'bilden'}
          </button>
        )}

        <div className="composer__caption">
          <Avatar src={user.profile_picture} name={user.username} size="sm" />
          <textarea
            ref={captionRef}
            value={content}
            onChange={e => setContent(e.target.value)}
            placeholder="Skriv något om bygget…"
            maxLength={2000}
            rows={3}
            aria-label="Bildtext"
          />
        </div>

        <div className="composer__tags">
          {HINTS.map(tag => (
            <button key={tag} type="button" onClick={() => addTag(tag)}>{tag}</button>
          ))}
        </div>

        <div className="choice">
          <button
            type="button"
            className={visibility === 'public' ? 'is-active' : ''}
            onClick={() => setVisibility('public')}
          >
            <strong>Alla på Cruze</strong>
            <span>Syns för alla, även de du inte känner</span>
          </button>
          <button
            type="button"
            className={visibility === 'friends' ? 'is-active' : ''}
            onClick={() => setVisibility('friends')}
          >
            <strong>Bara mina vänner</strong>
            <span>Syns bara för dina accepterade vänner</span>
          </button>
        </div>

        {error && <p className="notice notice--error">{error}</p>}

        <input
          type="file"
          accept="image/jpeg,image/png,image/webp,video/mp4,video/webm,video/quicktime"
          ref={fileRef}
          onChange={chooseFile}
          hidden
        />

        <button type="submit" className="btn btn--primary btn--block btn--lg" disabled={busy}>
          {busy ? 'Publicerar…' : 'Publicera'}
        </button>
      </form>
    </div>
  );
}
